import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Zap, Building2, Scale, Mail, Loader2, Sparkles } from 'lucide-react';
import { PublicNav } from '../components/layout/PublicNav';
import { useAuthStore } from '../store/authStore';
import { paymentsApi } from '../services/api';
import { isTwa } from '../utils/platform';

// Early-bird beta discount applied to every paid plan.
const EARLY_BIRD_OFF = 60;

// Every plan ships these — features are identical across tiers. The ONLY
// thing that changes between plans is how many researches you get and the
// price per research (which drops as you scale, capped at ₹25 on the smallest).
const includedFeatures = [
  'Full AI legal memoranda with verified citations — not summaries',
  'Supreme Court + High Court case law',
  'BNS / BNSS / BSA + IPC / CrPC coverage',
  'Document upload — scanned & regional-language (Malayalam, Hindi, Tamil…)',
  'Memos in English, हिंदी or മലയാളം — ask in your language',
  'Discuss, Test me & Opposing counsel on every memo',
  '17 draft types — guided interview, from a memo or standalone',
  'Case files with court diary, hearings & tasks',
  'PDF & Word download',
  'WhatsApp access',
];

const plans = [
  {
    name: 'Free',
    planKey: null,
    listPrice: null,
    price: '₹0',
    period: 'forever',
    perResearch: null,
    listPerResearch: null,
    description: 'Try it out — no card needed.',
    icon: Scale,
    color: 'border-legal-700 bg-legal-800/40',
    headerColor: 'text-white',
    priceColor: 'text-white',
    cta: 'Get Started Free',
    ctaStyle: 'border border-accent text-accent hover:bg-accent hover:text-legal-900',
    href: '/register',
    jobs: '3 researches / month',
  },
  {
    name: 'Junior',
    planKey: 'junior',
    listPrice: '₹250',
    price: '₹100',
    period: '/ month',
    perResearch: '₹10 / research',
    listPerResearch: '₹25',
    description: 'For advocates starting out.',
    icon: Sparkles,
    color: 'border-legal-700 bg-legal-800/40',
    headerColor: 'text-white',
    priceColor: 'text-white',
    cta: 'Start Junior',
    ctaStyle: 'border border-accent text-accent hover:bg-accent hover:text-legal-900',
    href: null,
    jobs: '10 researches / month',
  },
  {
    name: 'Pro',
    planKey: 'pro',
    listPrice: '₹1,000',
    price: '₹400',
    period: '/ month',
    perResearch: '₹8 / research',
    listPerResearch: '₹20',
    description: 'For individual advocates and researchers.',
    icon: Zap,
    color: 'border-accent bg-accent-50',
    headerColor: 'text-legal-900',
    priceColor: 'text-legal-900',
    badge: 'Most Popular',
    cta: 'Start Pro',
    ctaStyle: 'bg-accent text-legal-900 hover:bg-accent-600',
    href: null,
    jobs: '50 researches / month',
  },
  {
    name: 'Firm',
    planKey: 'firm',
    listPrice: '₹4,500',
    price: '₹1,800',
    period: '/ month',
    perResearch: '₹6 / research',
    listPerResearch: '₹15',
    description: 'For small firms with multiple advocates.',
    icon: Building2,
    color: 'border-legal-700 bg-legal-800/40',
    headerColor: 'text-white',
    priceColor: 'text-white',
    cta: 'Start Firm',
    ctaStyle: 'border border-accent text-accent hover:bg-accent hover:text-legal-900',
    href: null,
    jobs: '300 researches / month',
  },
  {
    name: 'Firm Pro',
    planKey: 'firm_pro',
    listPrice: '₹9,000',
    price: '₹3,600',
    period: '/ month',
    perResearch: '₹4.8 / research',
    listPerResearch: '₹12',
    description: 'For established firms with heavy research needs.',
    icon: Building2,
    color: 'border-legal-700 bg-legal-800/40',
    headerColor: 'text-white',
    priceColor: 'text-white',
    cta: 'Start Firm Pro',
    ctaStyle: 'border border-accent text-accent hover:bg-accent hover:text-legal-900',
    href: null,
    jobs: '750 researches / month',
  },
];

const faqs = [
  {
    q: 'What\'s actually different between the plans?',
    a: 'Only two things: how many researches you get each month, and the price per research. Every plan — Free included — has the exact same features. Bigger plans simply cost less per research, down from ₹25 on Junior to under ₹5 on Firm Pro.',
  },
  {
    q: 'What counts as one research?',
    a: 'Each time you submit a legal question and receive a full memorandum, that counts as one research. Downloading PDFs, chatting with a memo, viewing history, and uploading documents are not counted.',
  },
  {
    q: 'What is the early-bird discount?',
    a: 'Urava is in beta, so every plan is 60% off the list price for early users. The discounted price is what you pay today and is locked while the beta offer lasts.',
  },
  {
    q: 'What happens when I hit my monthly limit?',
    a: 'You\'ll be notified before you hit the limit. Once reached, you can upgrade your plan or wait for the next billing cycle — never more than ₹25 per research. We never cut off access to your existing research history.',
  },
  {
    q: 'Is my client data secure?',
    a: 'Yes. All research queries and documents are encrypted in transit and at rest. We do not use your data to train AI models. You can delete your data at any time.',
  },
  {
    q: 'Can I get a refund?',
    a: 'We\'re in beta and want your honest feedback — if a paid plan isn\'t working for you within 7 days, write to us and we\'ll refund it, no questions asked.',
  },
  {
    q: 'Does it cover the new BNS, BNSS and BSA?',
    a: 'Yes. Urava is fully updated with the Bharatiya Nyaya Sanhita, Bharatiya Nagarik Suraksha Sanhita, and Bharatiya Sakshya Adhiniyam replacing the IPC, CrPC, and Evidence Act.',
  },
  {
    q: 'What is Enterprise pricing?',
    a: 'For large firms, courts, or legal departments with more than 750 researches/month, we offer custom pricing with dedicated infrastructure. Contact us.',
  },
];

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function PricingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, updateUser } = useAuthStore();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState('');
  // In the Android (Play Store) build, payments stay web-only — Google Play
  // requires Play Billing for in-app digital purchases, so we never initiate a
  // Razorpay checkout here and point users to urava.app instead.
  const twa = isTwa();

  const handleUpgrade = async (planKey: string) => {
    setPaymentError('');
    if (!isAuthenticated) {
      navigate('/login?redirect=/pricing');
      return;
    }

    setLoadingPlan(planKey);
    try {
      // Create order BEFORE loading Razorpay — checkout.js patches global XHR/fetch
      // which breaks subsequent axios calls if loaded first
      let data: any;
      try {
        const res = await paymentsApi.createOrder(planKey);
        data = res.data;
      } catch (apiErr: any) {
        setPaymentError(`Order creation failed: ${apiErr.response?.data?.detail || apiErr.message}`);
        setLoadingPlan(null);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setPaymentError('Could not load payment SDK. Please disable any ad-blocker and try again.');
        setLoadingPlan(null);
        return;
      }

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'Urava Legal AI',
        description: `${data.plan_label} Plan — Monthly`,
        order_id: data.order_id,
        handler: async (response: { razorpay_payment_id: string; razorpay_order_id: string; razorpay_signature: string }) => {
          try {
            const { data: verified } = await paymentsApi.verifyPayment({
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              plan: planKey,
            });
            updateUser(verified.user);
            navigate('/app');
          } catch {
            setPaymentError('Payment succeeded but verification failed. Please contact support.');
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.full_name || '',
        },
        theme: { color: '#D4AF37' },
        modal: {
          ondismiss: () => setLoadingPlan(null),
        },
      };

      const rzp = new (window as any).Razorpay(options);
      rzp.on('payment.failed', () => {
        setPaymentError('Payment failed. Please try again.');
        setLoadingPlan(null);
      });
      rzp.open();
    } catch (err: any) {
      setPaymentError(`Payment error: ${err.message || 'Unknown error'}`);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-legal-900">
      <PublicNav />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center space-x-2 bg-accent/10 border border-accent/40 rounded-full px-4 py-1.5 text-xs font-semibold text-accent mb-6">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Beta early-bird — {EARLY_BIRD_OFF}% off every plan</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            One product. Every feature. <span className="text-accent">Pay less as you scale.</span>
          </h1>
          <p className="text-lg text-legal-300 max-w-2xl mx-auto">
            Every plan has the <span className="text-white font-semibold">exact same features</span> —
            the only difference is how many researches you get and the price per research.
            <br />Never more than <span className="text-accent font-semibold">₹25 per research</span>, dropping to under ₹5 on larger plans.
          </p>
        </div>

        {twa && (
          <div className="max-w-2xl mx-auto mb-10 p-4 bg-legal-800/40 border border-accent/30 rounded-xl text-center text-sm text-legal-300">
            To upgrade, open <span className="font-semibold text-accent">urava.app</span> in your
            browser and subscribe there. Your plan unlocks automatically in this app.
          </div>
        )}

        {/* Everything included — uniform across all plans */}
        <div className="max-w-4xl mx-auto mb-12 bg-legal-800/40 border border-legal-700 rounded-2xl p-6">
          <p className="text-center text-sm font-semibold text-accent uppercase tracking-wide mb-4">
            Included in every plan — Free to Firm Pro
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {includedFeatures.map((f) => (
              <li key={f} className="flex items-start space-x-2">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-accent" />
                <span className="text-sm text-legal-300">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {paymentError && (
          <div className="max-w-xl mx-auto mb-8 p-4 bg-red-900/40 border border-red-700 rounded-xl text-red-300 text-sm text-center">
            {paymentError}
          </div>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-20">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-2xl border p-6 flex flex-col relative ${plan.color}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-legal-900 text-xs font-bold px-3 py-1 rounded-full">
                  {plan.badge}
                </div>
              )}

              <div className="mb-4">
                <plan.icon className={`h-6 w-6 mb-3 ${plan.name === 'Pro' ? 'text-accent-600' : 'text-accent'}`} />
                <h3 className={`text-lg font-bold ${plan.headerColor}`}>{plan.name}</h3>
                <p className={`text-sm mt-1 ${plan.name === 'Pro' ? 'text-legal-600' : 'text-legal-400'}`}>
                  {plan.description}
                </p>
              </div>

              {plan.listPrice && (
                <div className="flex items-center space-x-2 mb-1">
                  <span className={`text-sm line-through ${plan.name === 'Pro' ? 'text-legal-500' : 'text-legal-500'}`}>
                    {plan.listPrice}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded">
                    {EARLY_BIRD_OFF}% off
                  </span>
                </div>
              )}

              <div className="mb-1">
                <span className={`text-3xl font-bold ${plan.priceColor}`}>{plan.price}</span>
                <span className={`text-sm ${plan.name === 'Pro' ? 'text-legal-600' : 'text-legal-400'}`}>
                  {' '}{plan.period}
                </span>
              </div>

              {plan.perResearch ? (
                <div className={`text-xs mb-3 ${plan.name === 'Pro' ? 'text-legal-600' : 'text-legal-400'}`}>
                  <span className="font-semibold text-accent">{plan.perResearch}</span>
                  {plan.listPerResearch && (
                    <span className="line-through ml-1.5 text-legal-500">{plan.listPerResearch}</span>
                  )}
                </div>
              ) : (
                <div className="text-xs mb-3 text-legal-500">No card required</div>
              )}

              <div className={`text-xs font-semibold mb-5 ${plan.name === 'Pro' ? 'text-accent-600' : 'text-accent'}`}>
                {plan.jobs}
              </div>

              {plan.planKey && twa ? (
                <div className={`text-center text-xs mt-auto py-2.5 px-3 rounded-lg border border-legal-700 ${plan.name === 'Pro' ? 'text-legal-600' : 'text-legal-400'}`}>
                  Subscribe at <span className="font-semibold text-accent">urava.app</span>
                </div>
              ) : plan.planKey ? (
                <button
                  onClick={() => handleUpgrade(plan.planKey!)}
                  disabled={loadingPlan === plan.planKey}
                  className={`flex items-center justify-center text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors mt-auto disabled:opacity-60 ${plan.ctaStyle}`}
                >
                  {loadingPlan === plan.planKey ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    plan.cta
                  )}
                </button>
              ) : (
                <Link
                  to={plan.href!}
                  className={`block text-center text-sm font-semibold py-2.5 px-4 rounded-lg transition-colors mt-auto ${plan.ctaStyle}`}
                >
                  {plan.cta}
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Enterprise CTA */}
        <div className="bg-legal-800/50 border border-legal-700 rounded-2xl p-8 text-center mb-20">
          <Building2 className="h-10 w-10 text-accent mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Enterprise — Custom Pricing</h2>
          <p className="text-legal-300 mb-6 max-w-xl mx-auto">
            Large firms, legal departments, or courts with 750+ researches/month.
            We'll design a plan around your usage, team size, and compliance requirements.
          </p>
          <a
            href="https://wa.me/918606600912?text=Hi%20Urava%2C%20I%27m%20interested%20in%20an%20enterprise%20plan"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 bg-accent text-legal-900 font-semibold px-6 py-3 rounded-lg hover:bg-accent-400 transition-colors"
          >
            <Mail className="h-4 w-4" />
            <span>Contact Us on WhatsApp</span>
          </a>
        </div>

        {/* Overage note */}
        <div className="bg-legal-800/30 border border-legal-700 rounded-xl p-5 mb-20 flex items-start space-x-4 max-w-3xl mx-auto">
          <Zap className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-semibold text-sm mb-1">The ₹25 cap</p>
            <p className="text-legal-400 text-sm">
              You'll never pay more than <span className="text-accent font-semibold">₹25 per research</span> on any plan —
              and far less on bigger plans. Need extra researches before your cycle resets?
              Just upgrade, or top up over WhatsApp at ₹25 each. No surprise bills.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-10">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-legal-700 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                <p className="text-legal-400 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
